import React from 'react';
import {
    Box,
    Select,
    MenuItem,
    FormControl,
    Typography,
    Chip,
    Paper,
    Divider,
    Button,
} from '@mui/material';
import { Edit, Refresh, CheckCircle, Delete, Check } from '@mui/icons-material';
import { PromptVersion } from '../types';

interface VersionSelectorProps {
    versions: PromptVersion[];
    selectedVersionId: string;
    onVersionSelect: (versionId: string) => void;
    onSetAsActive: (versionId: string) => void;
    onDeleteVersion?: (versionId: string) => void;
}

export const VersionSelector: React.FC<VersionSelectorProps> = ({
    versions,
    selectedVersionId,
    onVersionSelect,
    onSetAsActive,
    onDeleteVersion,
}) => {
    const selectedVersion = versions.find(v => v.id === selectedVersionId);

    const handleSetAsActive = () => {
        onSetAsActive(selectedVersionId);
    };

    return (
        <Paper
            elevation={0}
            sx={{
                p: 1.5,
                bgcolor: '#f8f9fa',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                <Refresh sx={{ fontSize: 20, color: 'primary.main' }} />
                <Typography variant="subtitle2" fontWeight={600} color="text.primary">
                    Select Version or Experiment
                </Typography>
            </Box>
            <Typography variant="caption" color="text.secondary" display="block" mb={2}>
                Choose a saved version or continue with an experiment.
            </Typography>

            <FormControl fullWidth size="small">
                <Select
                    value={selectedVersionId}
                    onChange={(e) => {
                        onVersionSelect(e.target.value as string);
                    }}
                    displayEmpty
                    renderValue={(selected) => {
                        const selectedVer = versions.find(v => v.id === selected);
                        if (!selectedVer) return 'Select version';

                        const formattedDate = new Date(selectedVer.createdAt).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                        });

                        return (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                {selectedVer.status === 'DRAFT' && (
                                    <Edit sx={{ fontSize: 16, color: 'text.secondary' }} />
                                )}
                                {selectedVer.experimentName && (
                                    <Typography component="span" fontWeight={500}>
                                        {selectedVer.experimentName}
                                    </Typography>
                                )}
                                <Typography component="span" fontWeight={500} color={selectedVer.experimentName ? 'text.secondary' : 'text.primary'}>
                                    v{selectedVer.version}
                                </Typography>
                                {selectedVer.isActive && (
                                    <Chip
                                        label="Active"
                                        color="primary"
                                        size="small"
                                        sx={{ height: 18, fontSize: '0.7rem' }}
                                    />
                                )}
                                <Typography component="span" variant="caption" color="text.secondary">
                                    — {formattedDate}
                                </Typography>
                            </Box>
                        );
                    }}
                    sx={{
                        bgcolor: 'white',
                        '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'divider',
                        },
                    }}
                >
                    {/* Saved Versions */}
                    <MenuItem disabled sx={{ opacity: 1, fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary' }}>
                        Saved Versions
                    </MenuItem>
                    {versions
                        .filter(v => v.status !== 'DRAFT')
                        .map((version) => {
                            const formattedDate = new Date(version.createdAt).toLocaleDateString('en-US', {
                                month: 'short',
                                day: 'numeric',
                            });

                            const isSelected = version.id === selectedVersionId;

                            return (
                                <MenuItem key={version.id} value={version.id}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%', pointerEvents: 'none' }}>
                                        {isSelected ? (
                                            <Check sx={{ fontSize: 16, color: 'primary.main' }} />
                                        ) : (
                                            <Box sx={{ width: 16 }} />
                                        )}
                                        {version.experimentName && (
                                            <Typography fontWeight={500}>{version.experimentName}</Typography>
                                        )}
                                        <Typography fontWeight={500} color={version.experimentName ? 'text.secondary' : 'text.primary'}>
                                            v{version.version}
                                        </Typography>
                                        {version.isActive && (
                                            <Chip
                                                label="Active"
                                                color="primary"
                                                size="small"
                                                sx={{ height: 18, fontSize: '0.7rem' }}
                                            />
                                        )}
                                        <Typography variant="caption" color="text.secondary">
                                            — {formattedDate}
                                        </Typography>
                                    </Box>
                                </MenuItem>
                            );
                        })}

                    {/* Your Experiments - Inside dropdown */}
                    {versions.filter(v => v.status === 'DRAFT').length > 0 && <Divider sx={{ my: 1 }} />}
                    {versions.filter(v => v.status === 'DRAFT').length > 0 && (
                        <MenuItem disabled sx={{ opacity: 1, fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary' }}>
                            Your Experiments
                        </MenuItem>
                    )}
                    {versions
                        .filter(v => v.status === 'DRAFT')
                        .map((draft) => {
                            const formattedDate = new Date(draft.createdAt).toLocaleDateString('en-US', {
                                month: 'short',
                                day: 'numeric',
                            });
                            const isSelected = draft.id === selectedVersionId;

                            return (
                                <MenuItem
                                    key={draft.id}
                                    value={draft.id}
                                    onClick={(e) => {
                                        // Check if click was on delete icon
                                        const target = e.target as HTMLElement;
                                        if (target.closest('[data-delete-icon]')) {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            return; // Don't select if clicking delete
                                        }
                                        onVersionSelect(draft.id);
                                    }}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                                        {isSelected ? (
                                            <Check sx={{ fontSize: 16, color: 'primary.main' }} />
                                        ) : (
                                            <Box sx={{ width: 16 }} />
                                        )}
                                        <Edit sx={{ fontSize: 16, color: 'primary.main' }} />
                                        <Typography fontWeight={500}>
                                            {draft.experimentName || 'New draft version'}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            from v{versions.find(v => v.id === draft.parentActiveVersionId)?.version || 1}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            • {formattedDate}
                                        </Typography>
                                        <Box
                                            data-delete-icon
                                            onClick={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                if (onDeleteVersion) {
                                                    onDeleteVersion(draft.id);
                                                }
                                            }}
                                            sx={{ ml: 'auto', display: 'flex', alignItems: 'center' }}
                                        >
                                            <Delete
                                                sx={{
                                                    fontSize: 16,
                                                    color: 'error.main',
                                                    cursor: 'pointer',
                                                    '&:hover': {
                                                        color: 'error.dark',
                                                    },
                                                }}
                                            />
                                        </Box>
                                    </Box>
                                </MenuItem>
                            );
                        })}
                </Select>
            </FormControl>

            {/* Set as Active Button - shown when non-active version is selected */}
            {selectedVersion && !selectedVersion.isActive && selectedVersion.status !== 'DRAFT' && (
                <Button
                    variant="outlined"
                    size="small"
                    startIcon={<CheckCircle />}
                    sx={{ mt: 2, textTransform: 'none', fontWeight: 500 }}
                    onClick={handleSetAsActive}
                >
                    Set as Active
                </Button>
            )}
        </Paper>
    );
};
